import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, retry, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  IApiReservation,
  IBookingRequest,
  IBulkDisponibilityRequest,
  ICreateDisponibilityRequest,
  IDispoResponse,
  IQuickReservationRequest,
  IQuickReservationResponse,
  IDiscountValidationResult,
  IUpdateAppointmentRequest,
  IUpdateDisponibilityRequest,
  IUserReservation
} from '../../interfaces/reservation.interface';

@Injectable({
  providedIn: 'root'
})
export class ReservationApiService {
  #http: HttpClient = inject(HttpClient);
  #apiBaseUrl: string = environment.apiUrl;

  getUserReservations(): Observable<IUserReservation[]> {
    return this.#http.get<IUserReservation[]>(`${this.#apiBaseUrl}/tools/reservation`);
  }

  createReservation(bookingData: IBookingRequest): Observable<IUserReservation> {
    return this.#http.post<IUserReservation>(`${this.#apiBaseUrl}/tools/reservation`, bookingData);
  }

  /**
   * Réservation express sans connexion (lien partageable par le coiffeur).
   * Envoyer soit couponCode soit promoCode, jamais les deux.
   */
  quickReservation(data: IQuickReservationRequest): Observable<IQuickReservationResponse> {
    return this.#http.post<IQuickReservationResponse>(`${this.#apiBaseUrl}/reservation/quick-reservation`, data);
  }

  validatePromoCode(promoCode: string, totalAmount: number, salonId?: number): Observable<IDiscountValidationResult> {
    return this.#http.post<{
      isValid: boolean;
      discountAmount: number;
      finalAmount: number;
      message: string;
    }>(`${this.#apiBaseUrl}/promo-codes/validate`, {
      promoCode,
      totalAmount,
      ...(salonId ? { salonId } : {})
    }).pipe(
      map(res => ({
        isValid: !!res.isValid,
        discountAmount: Number(res.discountAmount ?? 0),
        finalAmount: Number(res.finalAmount ?? totalAmount),
        message: res.message ?? ''
      }))
    );
  }

  validateCouponCode(couponCode: string, totalAmount: number): Observable<IDiscountValidationResult> {
    return this.#http.post<{
      isValid: boolean;
      discountAmount: number;
      message: string;
    }>(`${this.#apiBaseUrl}/coupons/validate`, {
      couponCode,
      totalAmount
    }).pipe(
      map(res => ({
        isValid: !!res.isValid,
        discountAmount: Number(res.discountAmount ?? 0),
        finalAmount: Number(totalAmount - Number(res.discountAmount ?? 0)),
        message: res.message ?? ''
      }))
    );
  }

  getHairdresserReservations(): Observable<IApiReservation[]> {
    return this.#http.get<IApiReservation[]>(`${this.#apiBaseUrl}/tools/reservation`);
  }

  getDisponibilities(hairdresserId: number): Observable<IDispoResponse[]> {
    return this.#http.get<IDispoResponse[]>(`${this.#apiBaseUrl}/tools/dispo?id=${hairdresserId}`).pipe(
      timeout(10000), // 10 secondes de timeout
      retry(1) // Retry une fois en cas d'échec
    );
  }

  postDisponibility(disponibility: ICreateDisponibilityRequest): Observable<any> {
    return this.#http.post(`${this.#apiBaseUrl}/tools/dispo`, disponibility);
  }

  addBulkDisponibilities(disponibilities: IBulkDisponibilityRequest): Observable<any> {
    return this.#http.post(`${this.#apiBaseUrl}/tools/dispo`, disponibilities);
  }

  updateDisponibility(disponibility: IUpdateDisponibilityRequest): Observable<any> {
    return this.#http.put(`${this.#apiBaseUrl}/tools/dispo`, disponibility);
  }

  deleteDisponibility(id: number): Observable<any> {
    return this.#http.delete(`${this.#apiBaseUrl}/tools/dispo?id=${id}`);
  }

  getTodayReservations(hairdresserId: number, date?: string): Observable<IUserReservation[]> {
    const targetDate = date || new Date().toISOString().split('T')[0];
    const params = `?hairdresserId=${hairdresserId}&date=${targetDate}`;
    return this.#http.get<IUserReservation[]>(`${this.#apiBaseUrl}/tools/reservation/today${params}`);
  }

  acceptReservation(reservationId: number): Observable<any> {
    return this.#http.put(`${this.#apiBaseUrl}/reservation/valid?id=${reservationId}`, {});
  }

  rejectReservation(reservationId: number): Observable<any> {
    return this.#http.put(`${this.#apiBaseUrl}/reservation/reject?id=${reservationId}`, {});
  }

  updateAppointment(updateData: IUpdateAppointmentRequest): Observable<any> {
    return this.#http.put(`${this.#apiBaseUrl}/reservation/update-apointment`, updateData);
  }

  valideUpdateAppointment(updateData: any): Observable<any> {
    return this.#http.put(`${this.#apiBaseUrl}/reservation/update-apointment`, updateData);
  }

  cancelReservation(reservationId: number): Observable<any> {
    return this.#http.put(`${this.#apiBaseUrl}/reservation/cancel?id=${reservationId}`, {});
  }

  finishReservation(reservationId: number): Observable<any> {
    return this.#http.put(`${this.#apiBaseUrl}/reservation/finish?id=${reservationId}`, {});
  }
}

